import { XStack, YStack } from '@mythical/ui'
import { ProductGrid as ProductGridType } from 'app/blocks/product-grid'
import { ContentCarousel } from 'app/components/content/ContentCarousel'
import { Button, ButtonLink } from 'app/components/primitive/Button'
import {
  IconChevronLeft,
  IconChevronRight,
} from 'app/components/primitive/Icon'
import {
  ProductCard,
  ProductCardProps,
} from 'app/components/primitive/ProductCard'
import Section from 'app/components/structure/Section'
import { useCarouselController } from 'app/hooks/use-carousel-controller'
import React, { useCallback } from 'react'

interface ProductGridProps {
  block: ProductGridType
  paddedX?: boolean
  darkTheme?: boolean
}

export const ProductGridBlock: React.FC<ProductGridProps> = ({
  block,
  paddedX = true,
  darkTheme = false,
}) => {
  const { title, ctaLabel, ctaHref, products } = block
  const controller = useCarouselController()

  if (!products || products.length === 0) {
    return null
  }

  const items: ProductCardProps[] = products.map((product) => ({
    backgroundColour: product.backgroundColour,
    description: product.description,
    discount: product.discount,
    forceDarkTheme: darkTheme,
    href: product.href,
    id: product.id,
    image: product.image.url,
    price: product.price,
    title: product.title,
  }))

  const renderItem = useCallback(
    (item: ProductCardProps) => <ProductCard {...item} />,
    [],
  )

  const keyExtractor = useCallback((item: ProductCardProps) => item.id, [])

  return (
    <Section
      paddedX={paddedX}
      title={title}
      customCta={
        <XStack gap="$2" ai="center">
          {ctaHref && ctaLabel && (
            <ButtonLink
              theme="secondary"
              size="$3.5"
              href={ctaHref}
              $sm={{ dsp: 'none' }}
            >
              {ctaLabel}
            </ButtonLink>
          )}

          <XStack gap="$2" $sm={{ dsp: 'none' }}>
            <Button
              bg="$bgInverted"
              onPress={controller.scrollToPrevious}
              disabled={!controller.canScrollPrevious}
              aria-label="Previous"
              icon={<IconChevronLeft size={16} color="$fgInverted" />}
              size="$3.5"
              circular
            />
            <Button
              bg="$bgInverted"
              onPress={controller.scrollToNext}
              disabled={!controller.canScrollNext}
              aria-label="Next"
              circular
              size="$3.5"
              icon={<IconChevronRight size={16} color="$fgInverted" />}
            />
          </XStack>
        </XStack>
      }
    >
      <YStack $gtSm={{ overflow: 'hidden' }}>
        <ContentCarousel
          controllerRef={controller.carouselRef}
          items={items}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
        />
      </YStack>
    </Section>
  )
}
